import type { ComponentProps } from "react";
import type { PostThread } from "@/features/study-group-stream/components/PostThread";
import type { SearchResultGroupContext } from "@/features/study-group-stream/utils/streamSearchResultTypes";
import type { ReactionType, StreamContent } from "@/lib/humhub-api/types";
import {
  buildPostCardViewModel,
  formatPostTimestampFromUtc,
} from "@/lib/viewModels/postCardViewModel";

export const getSearchResultPostObjectId = (post: StreamContent): number => {
  const candidateObjectId =
    post?.metadata?.object_id ?? post?.metadata?.id ?? post.id ?? 0;

  const parsed = Number(candidateObjectId);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : post.id;
};

export const buildSearchResultPostCard = ({
  post,
  groupContext,
  unknownAuthorText,
  isCurrentPostHighlighted,
  onReaction,
  isReactingPost,
}: {
  post: StreamContent;
  groupContext?: SearchResultGroupContext;
  unknownAuthorText: string;
  isCurrentPostHighlighted: boolean;
  onReaction?: (reaction: ReactionType | null) => Promise<void>;
  isReactingPost?: boolean;
}) =>
  buildPostCardViewModel({
    ids: {
      postId: getSearchResultPostObjectId(post),
      postContentId: post.id,
      studyGroupId: groupContext?.resolvedStudyGroupId,
    },
    author: {
      name: post.metadata?.created_by?.display_name || unknownAuthorText,
      id: post.metadata?.created_by?.id,
      avatarUrl: post.metadata?.created_by?.image_url || null,
    },
    timestamp: formatPostTimestampFromUtc(post.metadata?.created_at || ""),
    content: {
      body: post.model_attributes?.message || "",
      files: post.files || [],
    },
    status: {
      reactionsTotal: post.reactions?.total || 0,
      reactionsPreview: post.reactions?.last_reactions || [],
      reactionsBreakdown: post.reactions?.by_emotion,
      currentReaction: post.reactions?.current_user_emotion ?? null,
      commentsCount: post.comments?.total || 0,
      isHighlighted: isCurrentPostHighlighted,
      isReacting: isReactingPost,
    },
    handlers: {
      onReaction,
    },
  });

type SearchResultCommentSectionProps = ComponentProps<
  typeof PostThread
>["commentSectionProps"];
type SearchResultEventLinkPreviewProps = ComponentProps<
  typeof PostThread
>["eventLinkPreviewProps"];

type SearchResultThreadMeta = {
  post: StreamContent;
  matchedCommentIds: number[];
  isCurrentPostMatched: boolean;
  groupContext?: SearchResultGroupContext;
  postObjectId: number;
  isFirstResultFamily: boolean;
};

export const buildSearchResultThreadCommentSectionProps = ({
  post,
  matchedCommentIds,
  isCurrentPostMatched,
  groupContext,
  postObjectId,
  isFirstResultFamily,
}: SearchResultThreadMeta): SearchResultCommentSectionProps => ({
  postContentId: post.id,
  postObjectId,
  studyGroupId: groupContext?.resolvedStudyGroupId,
  selectedTopics: post.topics,
  currentUser: null,
  highlightCommentIds: matchedCommentIds,
  shouldScrollToComment:
    isFirstResultFamily &&
    !(isCurrentPostMatched && matchedCommentIds.length > 0),
});

export const buildSearchResultEventLinkPreviewProps = ({
  groupContext,
}: {
  groupContext?: SearchResultGroupContext;
}): SearchResultEventLinkPreviewProps => ({
  currentStudyGroupId: groupContext?.studyGroupIdText,
});
